// @ts-check
import React, { useCallback } from 'react';
import { useMutation } from 'react-query';
import api from 'utils/api';
import { Modal } from 'preferral-ui/components/Modal';
import { Button } from 'preferral-ui/components/Button';
import FlashMessageActions from 'shared/actions/FlashMessageActions';
import TagIcon from '@heroicons/react/outline/TagIcon';

/** @typedef {{
  isOpen: boolean;
  onClose(): void;
  referralTagId: string;
  onSuccess?(): void;
}} DeleteReferralTagModalProps */

/** @type { React.FC<DeleteReferralTagModalProps> } */
export const DeleteReferralTagModal = (props) => {
  const { isOpen, onClose, referralTagId, onSuccess } = props;

  const mutation = useMutation((referralTagId) =>
    api
      .delete(`/agent_portal/api/referral_tags/${referralTagId}`)
      .then((resp) => resp.data)
  );

  const onSubmit = useCallback(
    (referralTagId) => {
      return mutation.mutateAsync(referralTagId).then((_data) => {
        // it worked...
        FlashMessageActions.flashSuccess({ message: 'Referral tag deleted' });
        onSuccess()
      });
    },
    [onSuccess, mutation]
  );

  return (
    <Modal
      className="_DeleteReferralTagModal max-w-lg"
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      <div className=" p-4">
        <div className="sm:flex sm:items-start">
          <div className="sm:mx-0 sm:h-10 sm:w-10 flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full">
            <TagIcon
              className="w-6 h-6 text-red-600"
              aria-hidden="true"
            />
          </div>
          <div className="sm:mt-0 sm:ml-4 sm:text-left mt-3 text-center">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Delete Referral Tag
            </h3>
            <div className="mt-2">
              <p className="text-sm text-gray-500">
                Are you sure you want to delete this referral tag? The tag will be removed from all referrals.
              </p>
            </div>
          </div>
        </div>
        <div className="sm:mt-4 sm:flex sm:flex-row-reverse mt-5">
          <Button
            type="button"
            kind="primary"
            color="red"
            isLoading={mutation.isLoading}
            className="sm:ml-3"
            onClick={() => onSubmit(referralTagId)}
          >
            Delete Referral Tag
          </Button>
          <Button
            type="button"
            className="hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm"
            onClick={onClose}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};
